import { useLatestCallback } from '@marketplace-web/shared/ui-state-util'
import {
  DynamicFilterReducerAction,
  useCatalogContext,
} from '@marketplace-web/catalog/catalog-page-data'
import {
  SelectedDynamicFilterModel,
  DynamicFilterModel,
  findFilterOptionById,
  PriceRange,
} from '@marketplace-web/catalog/catalog-data'

type UseFacetChangeHandlerProps = {
  dynamicFiltersList: Array<DynamicFilterModel>
  setTotalItemsCount: (count: number | null) => void
  fetchDynamicFiltersFacets: (params: any) => Promise<any> | void
  selectedTemporaryCatalogIds?: Array<number>
  selectedTemporaryPriceRange?: PriceRange
}

const useFacetChangeHandler = ({
  dynamicFiltersList,
  setTotalItemsCount,
  fetchDynamicFiltersFacets,
  selectedTemporaryCatalogIds,
  selectedTemporaryPriceRange,
}: UseFacetChangeHandlerProps) => {
  const { dispatch } = useCatalogContext()

  return useLatestCallback(
    (currentSelectedFilters: Array<SelectedDynamicFilterModel>, type: string) => {
      const filteredSelections = currentSelectedFilters.filter(
        selectedFilter => selectedFilter.type === type,
      )

      if (!filteredSelections.length) {
        dispatch({
          type: DynamicFilterReducerAction.SET_DYNAMIC_FILTER_FACETS_COUNTS,
          payload: { filterFacetsCount: null },
        })

        setTotalItemsCount(null)

        return
      }
      const matchingFilter = dynamicFiltersList.find(filter => filter.type === type)
      if (!matchingFilter) {
        dispatch({
          type: DynamicFilterReducerAction.SET_DYNAMIC_FILTER_FACETS_COUNTS,
          payload: { filterFacetsCount: null },
        })

        setTotalItemsCount(null)

        return
      }

      let newTotal: number | null = 0 // accumulates known counts, null if unknown or no valid counts
      let isCountUnknown: boolean = false // flags unmatched options to nullify total
      let hasKnown: boolean = false // tracks if any known count (non-null) was found such as 0
      if (matchingFilter.isAsyncFacet) {
        fetchDynamicFiltersFacets({
          selectedDynamicFilters: currentSelectedFilters,
          selectedTemporaryCatalogIds,
          selectedTemporaryPriceRange,
          filterCode: type,
        })

        setTotalItemsCount(null)
      } else {
        filteredSelections.forEach(selectedFilter => {
          if (!selectedFilter.ids.length) return

          selectedFilter.ids.forEach(id => {
            const matchedOption = findFilterOptionById(matchingFilter.options, id)
            if (!matchedOption) {
              isCountUnknown = true

              return
            }

            const count = matchedOption.itemsCount
            if (count != null) {
              newTotal! += count
              hasKnown = true
            }
          })
        })

        // set null if unmatched options exist or no valid counts were added
        setTotalItemsCount(isCountUnknown || !hasKnown ? null : newTotal)
      }
    },
  )
}

export default useFacetChangeHandler
